import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

// 페이지 애니메이션 설정
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.3,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

function StatusIdCardCropPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationNumber } = useParams();
  const [originalImage, setOriginalImage] = useState(null);
  const [crop, setCrop] = useState({
    unit: "%",
    width: 80,
    height: 50,
    x: 10,
    y: 25,
    aspect: 325 / 208, // 신분증 가로세로 비율 (IdCardCameraPage에서 사용하는 비율)
  });
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);

  useEffect(() => {
    // location.state에서 이미지 가져오기 또는 로컬 스토리지에서 가져오기
    const image = location.state?.originalImage;
    if (image) {
      setOriginalImage(image);
    } else {
      // 이미지가 없는 경우 업로드 페이지로 돌아가기
      navigate(`/status/id-card/${applicationNumber}`);
    }
  }, [location, navigate, applicationNumber]);

  // 크롭된 이미지 생성 함수
  const generateCroppedImage = () => {
    if (!completedCrop || !imgRef.current) return;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = 325; // 최종 출력 너비
    canvas.height = 208; // 최종 출력 높이

    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );

    return canvas.toDataURL("image/jpeg", 0.95);
  };

  // 완료 버튼 클릭 시
  const handleCropComplete = () => {
    const croppedImage = generateCroppedImage();
    if (croppedImage) {
      // 로컬 스토리지에 저장
      localStorage.setItem("temp_captured_image", croppedImage);

      // StatusIdCardSubmitPage로 이동
      navigate(`/status/id-card/${applicationNumber}`, {
        state: {
          fromCrop: true,
          capturedImage: { imageDataURL: croppedImage },
        },
      });
    }
  };

  // 취소 버튼 클릭 시
  const handleCancel = () => {
    navigate(`/status/id-card/${applicationNumber}`);
  };

  return (
    <motion.div
      className="crop-page"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="crop-header">
        <button className="crop-back-button" onClick={handleCancel}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 18L9 12L15 6"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <h1>신분증 영역 설정</h1>
        <button className="crop-close-button" onClick={handleCancel}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6 6L18 18"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className="crop-container">
        <div className="crop-instructions">
          <p>신분증이 프레임 안에 완전히 들어오도록 조정해주세요</p>
        </div>

        {originalImage && (
          <ReactCrop
            src={originalImage}
            crop={crop}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
            aspect={325 / 208}
            className="crop-image-container"
          >
            <img
              ref={imgRef}
              src={originalImage}
              alt="신분증 이미지"
              style={{ maxWidth: "100%", maxHeight: "70vh" }}
            />
          </ReactCrop>
        )}

        <canvas ref={previewCanvasRef} style={{ display: "none" }} />
      </div>

      <div className="crop-controls">
        <button className="crop-cancel-button" onClick={handleCancel}>
          취소
        </button>
        <button className="crop-complete-button" onClick={handleCropComplete}>
          완료
        </button>
      </div>

      <style jsx="true">{`
        .crop-page {
          display: flex;
          flex-direction: column;
          height: 100vh;
          background-color: #000;
          color: #fff;
        }

        .crop-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 15px;
          background-color: #000;
          position: relative;
          z-index: 10;
        }

        .crop-header h1 {
          font-size: 18px;
          margin: 0;
          text-align: center;
          flex-grow: 1;
        }

        .crop-back-button,
        .crop-close-button {
          background: none;
          border: none;
          cursor: pointer;
          color: white;
          padding: 5px;
        }

        .crop-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 10px;
          overflow: hidden;
        }

        .crop-instructions {
          margin-bottom: 15px;
          text-align: center;
          color: #fff;
          font-size: 16px;
        }

        .crop-controls {
          display: flex;
          justify-content: space-between;
          padding: 15px 20px;
          background-color: #000;
        }

        .crop-cancel-button,
        .crop-complete-button {
          padding: 10px 20px;
          border-radius: 5px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          width: 48%;
        }

        .crop-cancel-button {
          background-color: #333;
          color: white;
          border: 1px solid #555;
        }

        .crop-complete-button {
          background-color: #3855e2;
          color: white;
          border: none;
        }

        .crop-image-container {
          max-width: 100%;
          max-height: 70vh;
          display: flex;
          justify-content: center;
        }
      `}</style>
    </motion.div>
  );
}

export default StatusIdCardCropPage;
